import React from 'react';
import { Modal, Table, Spinner } from 'react-bootstrap';
import { usePerformanceDayDetail } from '../../hooks/usePerformanceData';
import useIsMobile from '../../hooks/useIsMobile';
import BottomSheet from '../shared/BottomSheet';
import { MOBILE_UI } from '../../constants/mobileUi';
import { parseDateKey } from './PerformanceDashboardCalendar';

const formatCurrency = (v) =>
  v >= 0 ? `+$${v.toFixed(2)}` : `-$${Math.abs(v).toFixed(2)}`;

const formatPercent = (v) => `${(v * 100).toFixed(1)}%`;

const formatTradePrice = (price) => (price != null ? `$${Number(price).toFixed(2)}` : '—');

const formatTradeTime = (str) => {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatTradeDuration = (mins) => {
  if (mins == null || mins < 0) return '—';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
};

function formatDayHeader(dateKey) {
  const d = parseDateKey(dateKey);
  if (!d) return dateKey || 'Day';
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDayHeaderShort(dateKey) {
  const d = parseDateKey(dateKey);
  if (!d) return dateKey || 'Day';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Stacked trade rows: the seven-column desktop table cannot fit a phone without scrolling sideways. */
function MobileDayTrades({ trades, colors, onSymbolClick }) {
  return (
    <div data-testid="performance-day-mobile-trades">
      {trades.map((t, i) => {
        const pnlColor = t.pnl == null ? colors.textMuted : t.pnl >= 0 ? colors.chartUp : colors.chartDown;
        const rowKey = t.tradeId || `${t.symbol}-${t.exitTime || i}`;
        return (
          <div
            key={rowKey}
            style={{
              padding: '10px 0',
              borderBottom: `1px solid ${colors.borderLight || colors.border}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <button
                type="button"
                onClick={() => onSymbolClick?.(t.symbol)}
                disabled={!onSymbolClick}
                style={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  fontSize: 15,
                  fontWeight: 700,
                  color: onSymbolClick ? colors.primary : colors.text,
                  cursor: onSymbolClick ? 'pointer' : 'default',
                }}
              >
                {t.symbol}
              </button>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: pnlColor,
                  fontVariantNumeric: 'tabular-nums',
                  flex: '0 0 auto',
                }}
              >
                {t.pnl != null ? formatCurrency(t.pnl) : '—'}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                fontSize: MOBILE_UI.font.meta,
                color: colors.textMuted,
              }}
            >
              <span style={{ textTransform: 'capitalize' }}>{t.direction || '—'}</span>
              <span>·</span>
              <span>{t.quantity ?? '—'} shares</span>
              <span>·</span>
              <span>{formatTradeDuration(t.durationMinutes)}</span>
            </div>
            <div
              style={{
                fontSize: MOBILE_UI.font.meta,
                color: colors.textMuted,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatTradePrice(t.entryPrice)} → {formatTradePrice(t.exitPrice)}
              {t.exitTime ? ` · ${formatTradeTime(t.exitTime)}` : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const PerformanceCalendarDayModal = ({
  show,
  onHide,
  dateKey,
  daySummary,
  accountId,
  paperTrading,
  colors,
  onSymbolClick,
}) => {
  const { data, isLoading, isError } = usePerformanceDayDetail({
    dateKey,
    accountId,
    paperTrading,
    enabled: show && !!dateKey,
  });

  const isMobile = useIsMobile();
  const summary = data?.summary ?? daySummary ?? { pnl: 0, trades: 0, winRate: 0 };
  const trades = data?.trades ?? [];
  const headerAccent =
    summary.trades === 0 ? colors.textMuted : summary.pnl >= 0 ? colors.chartUp : colors.chartDown;

  const summaryStats = (
    <div
      className={`performance-calendar-day-summary${
        isMobile ? ' performance-calendar-day-summary--sheet' : ''
      }`}
    >
      <div className="performance-calendar-day-summary-stat">
        <span className="performance-calendar-day-summary-label">P&amp;L</span>
        <span
          className="performance-calendar-day-summary-value"
          style={{ color: summary.trades > 0 ? headerAccent : colors.textMuted }}
        >
          {summary.trades > 0 ? formatCurrency(summary.pnl) : '—'}
        </span>
      </div>
      <div className="performance-calendar-day-summary-stat">
        <span className="performance-calendar-day-summary-label">Trades</span>
        <span className="performance-calendar-day-summary-value" style={{ color: colors.text }}>
          {summary.trades}
        </span>
      </div>
      <div className="performance-calendar-day-summary-stat">
        <span className="performance-calendar-day-summary-label">Win rate</span>
        <span className="performance-calendar-day-summary-value" style={{ color: colors.text }}>
          {summary.trades > 0 ? formatPercent(summary.winRate) : '—'}
        </span>
      </div>
    </div>
  );

  const loadStates = (
    <>
      {isLoading && (
        <div className="performance-calendar-day-loading">
          <Spinner animation="border" size="sm" style={{ color: colors.primary }} />
          <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Loading trades…</span>
        </div>
      )}

      {isError && !isLoading && (
        <p style={{ color: colors.chartDown, fontSize: '0.85rem', marginBottom: 0 }}>
          Could not load trades for this day.
        </p>
      )}

      {!isLoading && !isError && trades.length === 0 && (
        <p style={{ color: colors.textMuted, fontSize: '0.875rem', marginBottom: 0 }}>
          No closed trades on this day.
        </p>
      )}
    </>
  );

  if (isMobile) {
    return (
      <BottomSheet
        show={show}
        onHide={onHide}
        title={formatDayHeaderShort(dateKey)}
        testId="performance-calendar-day-sheet"
        closeLabel="Close day details"
      >
        {summaryStats}
        {loadStates}
        {!isLoading && trades.length > 0 ? (
          <MobileDayTrades trades={trades} colors={colors} onSymbolClick={onSymbolClick} />
        ) : null}
      </BottomSheet>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      scrollable
      className="trade-risk-quick-setup-modal performance-calendar-day-modal"
      dialogClassName="trade-risk-quick-setup-dialog performance-calendar-day-dialog"
      contentClassName="trade-risk-quick-setup-content"
    >
      <Modal.Header closeButton style={{ borderColor: colors.border }}>
        <Modal.Title style={{ color: colors.text, fontSize: '1.1rem' }}>
          {formatDayHeader(dateKey)}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ color: colors.text }}>
        {summaryStats}
        {loadStates}

        {!isLoading && trades.length > 0 && (
          <Table
            bordered
            hover
            size="sm"
            className="performance-calendar-day-trades recent-trades-table"
            style={{
              fontSize: '0.8rem',
              marginBottom: 0,
              marginTop: '16px',
              backgroundColor: colors.bgPrimary,
              color: colors.text,
              '--bs-table-bg': colors.bgPrimary,
              '--bs-table-color': colors.text,
              '--bs-table-hover-bg': colors.bgSecondary,
              '--bs-table-hover-color': colors.text,
              '--bs-table-border-color': colors.border,
            }}
          >
            <thead>
              <tr style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase' }}>
                <th style={{ color: colors.textMuted }}>Symbol</th>
                <th style={{ color: colors.textMuted }}>Side</th>
                <th style={{ color: colors.textMuted }}>Entry</th>
                <th style={{ color: colors.textMuted }}>Exit</th>
                <th style={{ color: colors.textMuted }}>Qty</th>
                <th style={{ color: colors.textMuted }}>Duration</th>
                <th style={{ textAlign: 'right', color: colors.textMuted }}>P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => {
                const pnlColor =
                  t.pnl == null ? colors.textMuted : t.pnl >= 0 ? colors.chartUp : colors.chartDown;
                const rowKey = t.tradeId || `${t.symbol}-${t.exitTime || i}`;
                return (
                  <tr key={rowKey}>
                    <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <strong
                        role={onSymbolClick ? 'button' : undefined}
                        tabIndex={onSymbolClick ? 0 : undefined}
                        onClick={() => onSymbolClick?.(t.symbol)}
                        onKeyDown={(e) => {
                          if (!onSymbolClick) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSymbolClick(t.symbol);
                          }
                        }}
                        style={{
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          color: onSymbolClick ? colors.primary : colors.text,
                          cursor: onSymbolClick ? 'pointer' : 'default',
                        }}
                        title={onSymbolClick ? `Open chart for ${t.symbol}` : undefined}
                      >
                        {t.symbol}
                      </strong>
                    </td>
                    <td style={{ verticalAlign: 'middle', color: colors.text, textTransform: 'capitalize' }}>
                      {t.direction || '—'}
                    </td>
                    <td style={{ verticalAlign: 'middle', color: colors.text }}>
                      <div>{formatTradePrice(t.entryPrice)}</div>
                      <div style={{ fontSize: '0.68rem', color: colors.textMuted }}>
                        {formatTradeTime(t.entryTime)}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', color: colors.text }}>
                      <div>{formatTradePrice(t.exitPrice)}</div>
                      <div style={{ fontSize: '0.68rem', color: colors.textMuted }}>
                        {formatTradeTime(t.exitTime)}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', color: colors.text }}>{t.quantity ?? '—'}</td>
                    <td style={{ verticalAlign: 'middle', color: colors.text, whiteSpace: 'nowrap' }}>
                      {formatTradeDuration(t.durationMinutes)}
                    </td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'right', fontWeight: 700, color: pnlColor }}>
                      {t.pnl != null ? formatCurrency(t.pnl) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PerformanceCalendarDayModal;
